import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useCompany } from "../../context/CompanyContext";
import {
  createAccount,
  getAccounts,
  updateAccount,
} from "../services/crmAccountsService";
import "../styles/crm.css";

const defaultForm = {
  name: "",
  industry: "",
  email: "",
  phone: "",
  account_type: "prospect",
  status: "prospect",
};

const AccountsPage = () => {
  const { companyId } = useCompany();
  const [accounts, setAccounts] = useState([]);
  const [form, setForm] = useState(defaultForm);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editingId, setEditingId] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;

    setLoading(true);
    setMessage("");

    try {
      const rows = await getAccounts(companyId);
      setAccounts(rows);
    } catch (error) {
      setMessage(error.message || "Failed to load accounts");
    } finally {
      setLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleFormChange = useCallback((field, value) => {
    setForm((previous) => ({
      ...previous,
      [field]: value,
    }));
  }, []);

  const resetForm = useCallback(() => {
    setForm(defaultForm);
    setEditingId("");
  }, []);

  const handleEdit = useCallback((row) => {
    setEditingId(row.id);
    setForm({
      name: row.name || "",
      industry: row.industry || "",
      email: row.email || "",
      phone: row.phone || "",
      account_type: row.account_type || "prospect",
      status: row.status || "prospect",
    });
    setMessage("");
  }, []);

  const handleSave = useCallback(async () => {
    if (!companyId || !form.name.trim()) return;

    setSaving(true);
    setMessage("");

    try {
      const payload = {
        company_id: companyId,
        name: form.name.trim(),
        industry: form.industry.trim() || null,
        email: form.email.trim() || null,
        phone: form.phone.trim() || null,
        account_type: form.account_type,
        status: form.status,
      };

      if (editingId) {
        await updateAccount(editingId, companyId, payload);
      } else {
        await createAccount(payload);
      }

      resetForm();
      await load();
    } catch (error) {
      setMessage(error.message || "Failed to save account");
    } finally {
      setSaving(false);
    }
  }, [companyId, form, editingId, load, resetForm]);

  const filteredAccounts = useMemo(() => {
    const searchValue = search.trim().toLowerCase();

    return accounts.filter((row) => {
      const matchesSearch =
        !searchValue ||
        String(row.name || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(row.email || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(row.phone || "")
          .toLowerCase()
          .includes(searchValue) ||
        String(row.industry || "")
          .toLowerCase()
          .includes(searchValue);

      const matchesType =
        typeFilter === "all" || String(row.account_type || "") === typeFilter;

      const matchesStatus =
        statusFilter === "all" || String(row.status || "") === statusFilter;

      return matchesSearch && matchesType && matchesStatus;
    });
  }, [accounts, search, typeFilter, statusFilter]);

  return (
    <div>
      <h2 className="crm-page-title">Accounts</h2>

      {message ? <div className="crm-message">{message}</div> : null}

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <h3 className="crm-page-title" style={{ fontSize: "20px" }}>
          {editingId ? "Edit Account" : "Create Account"}
        </h3>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Account Name</label>
            <input
              className="crm-input"
              value={form.name}
              onChange={(event) => handleFormChange("name", event.target.value)}
              placeholder="Enter account name"
            />
          </div>

          <div className="crm-form-group">
            <label>Industry</label>
            <input
              className="crm-input"
              value={form.industry}
              onChange={(event) =>
                handleFormChange("industry", event.target.value)
              }
              placeholder="Enter industry"
            />
          </div>
        </div>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Email</label>
            <input
              className="crm-input"
              value={form.email}
              onChange={(event) =>
                handleFormChange("email", event.target.value)
              }
              placeholder="Enter email"
            />
          </div>

          <div className="crm-form-group">
            <label>Phone</label>
            <input
              className="crm-input"
              value={form.phone}
              onChange={(event) =>
                handleFormChange("phone", event.target.value)
              }
              placeholder="Enter phone"
            />
          </div>
        </div>

        <div className="crm-form-row">
          <div className="crm-form-group">
            <label>Account Type</label>
            <select
              className="crm-select"
              value={form.account_type}
              onChange={(event) =>
                handleFormChange("account_type", event.target.value)
              }
            >
              <option value="prospect">Prospect</option>
              <option value="customer">Customer</option>
              <option value="partner">Partner</option>
            </select>
          </div>

          <div className="crm-form-group">
            <label>Status</label>
            <select
              className="crm-select"
              value={form.status}
              onChange={(event) =>
                handleFormChange("status", event.target.value)
              }
            >
              <option value="prospect">Prospect</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="blacklisted">Blacklisted</option>
            </select>
          </div>
        </div>

        <div className="crm-inline-actions">
          <button
            className="crm-primary-btn"
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? "Saving..."
              : editingId
              ? "Update Account"
              : "Add Account"}
          </button>

          {editingId ? (
            <button
              className="crm-secondary-btn"
              onClick={resetForm}
              disabled={saving}
            >
              Cancel Edit
            </button>
          ) : null}
        </div>
      </div>

      <div className="crm-table-card" style={{ marginBottom: "20px" }}>
        <div className="crm-filter-row">
          <div className="crm-form-group">
            <label>Search</label>
            <input
              className="crm-input"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search accounts"
            />
          </div>

          <div className="crm-form-group">
            <label>Type</label>
            <select
              className="crm-select"
              value={typeFilter}
              onChange={(event) => setTypeFilter(event.target.value)}
            >
              <option value="all">All</option>
              <option value="prospect">Prospect</option>
              <option value="customer">Customer</option>
              <option value="partner">Partner</option>
            </select>
          </div>

          <div className="crm-form-group">
            <label>Status</label>
            <select
              className="crm-select"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="all">All</option>
              <option value="prospect">Prospect</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="blacklisted">Blacklisted</option>
            </select>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="crm-message">Loading accounts...</div>
      ) : filteredAccounts.length === 0 ? (
        <div className="crm-message">No accounts found.</div>
      ) : (
        <div className="crm-table-card">
          <div className="crm-data-table-wrap">
            <table className="crm-data-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Industry</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {filteredAccounts.map((row) => (
                  <tr key={row.id}>
                    <td>{row.name || "N/A"}</td>
                    <td>{row.industry || "N/A"}</td>
                    <td>{row.email || "N/A"}</td>
                    <td>{row.phone || "N/A"}</td>
                    <td>{row.account_type || "N/A"}</td>
                    <td>{row.status || "N/A"}</td>
                    <td>
                      <div className="crm-inline-actions">
                        <button
                          className="crm-small-btn alt"
                          onClick={() => handleEdit(row)}
                        >
                          Edit
                        </button>
                        <Link
                          className="crm-small-btn"
                          to={`/crm/accounts/${row.id}`}
                        >
                          Open
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountsPage;
